import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { IoCloseOutline } from "react-icons/io5";
import {
  createNewRole,
  fetchAllPermissions,
} from "../../../../../../../services/settings.services";
import { showSnackBar } from "../../../../../../../store/common/snackBarSlice";
import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";

const filter = createFilterOptions();

const WAREHOUSE_PERMISSION_ROOTS = [
  "shipper.access.product",
  "shipper.access.order",
  "shipper.access.payment",
];
const WAREHOUSE_ALLOWED_CHILDREN = [
  "shipper.access.product.view",
  "shipper.access.order.view",
  "shipper.access.payment.view",
];

const SHIPPER_PERMISSION_ROOTS = [
  "shipper.access.shipment",
  // "shipper.access.invoice",  // no /Invoices route registered
  // "shipper.access.pickup",   // no /Pickup route registered
  // "shipper.access.tracking", // no /Tracking route registered
  "shipper.access.product",
  // "shipper.access.carrier", // hidden for now
  "shipper.access.order",
  "shipper.access.payment",
];
const SHIPPER_ALLOWED_CHILDREN = [
  "shipper.access.shipment.view",
  // "shipper.access.invoice.view",  // no /Invoices route registered
  // "shipper.access.pickup.view",   // no /Pickup route registered
  // "shipper.access.tracking.view", // no /Tracking route registered
  "shipper.access.product.view",
  // "shipper.access.carrier.view", // hidden for now
  "shipper.access.order.view",
  "shipper.access.payment.view",
];

// Keep hidden children in the data (marked `hidden: true`) so toggling a
// visible permission can also toggle its hidden siblings.
const filterPermissionTree = (allPermissions, roots, allowedChildren) =>
  allPermissions
    .filter((p) => roots.includes(p.name))
    .map((p) => ({
      ...p,
      children: (p.children ?? []).map((c) => ({
        ...c,
        hidden: !allowedChildren.includes(c.name),
      })),
    }));

const autocompleteSx = {
  "& .MuiOutlinedInput-root": {
    height: "40px",
    padding: "0 9px",
    fontSize: "14px",
    borderRadius: "8px",
    "& fieldset": { borderColor: "var(--color-ui-border)" },
    "&:hover fieldset": { borderColor: "var(--color-ui-border)" },
    "&.Mui-focused fieldset": { borderColor: "#60a5fa", borderWidth: "1px" },
  },
  "& .MuiAutocomplete-input": { padding: "0 !important" },
};

export default function RoleDropdownWithCreate({
  value,
  onChange,
  roles,
  setRoles,
}) {
  const { t } = useTranslation("settings");
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", permissions: [] });
  const [permissions, setPermissions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  const allModuleIds = (perms) =>
    perms.flatMap((p) => [p.id, ...(p.children ?? []).map((c) => c.id)]);

  const isWarehouse = user?.role === "warehouseUser";
  const isShipper =
    user?.role === "Shipper" ||
    user?.role === "ShipperUser" ||
    user?.role === "oneTimeShipper";

  const closeModal = () => {
    setModalOpen(false);
    setNewRole({ name: "", permissions: [] });
  };

  const fetchPermissions = async () => {
    setLoadingPermissions(true);
    const saved = await fetchAllPermissions();
    if (saved?.error) {
      setLoadingPermissions(false);
      dispatch(
        showSnackBar({
          text: t("roles.permissionsLoadFailed"),
          severity: "error",
        })
      );
      return;
    }
    const all = saved?.data?.result?.permissions?.data ?? [];
    let filtered;
    if (isWarehouse) {
      filtered = filterPermissionTree(
        all,
        WAREHOUSE_PERMISSION_ROOTS,
        WAREHOUSE_ALLOWED_CHILDREN
      );
    } else if (isShipper) {
      filtered = filterPermissionTree(
        all,
        SHIPPER_PERMISSION_ROOTS,
        SHIPPER_ALLOWED_CHILDREN
      );
    } else {
      filtered = all;
    }
    setPermissions(filtered);
    // Default: all permissions selected when modal opens.
    setNewRole((prev) => ({ ...prev, permissions: allModuleIds(filtered) }));
    setLoadingPermissions(false);
  };

  useEffect(() => {
    if (modalOpen) fetchPermissions();
  }, [modalOpen]);

  const handleParentChange = (e, ids) => {
    setNewRole((prev) => ({
      ...prev,
      permissions: e.target.checked
        ? Array.from(new Set([...prev.permissions, ...ids]))
        : prev.permissions.filter((id) => !ids.includes(id)),
    }));
  };

  const handleChildChange = (e, id, allChildren) => {
    // Also toggle hidden siblings so the role stores every sub-permission.
    const hiddenSiblingIds = (allChildren ?? [])
      .filter((c) => c.hidden)
      .map((c) => c.id);
    const idsToToggle = [id, ...hiddenSiblingIds];
    setNewRole((prev) => ({
      ...prev,
      permissions: e.target.checked
        ? Array.from(new Set([...prev.permissions, ...idsToToggle]))
        : prev.permissions.filter((p) => !idsToToggle.includes(p)),
    }));
  };

  const allIds = allModuleIds(permissions);
  const isAllSelected =
    allIds.length > 0 && allIds.every((id) => newRole.permissions.includes(id));

  const toggleSelectAll = () => {
    setNewRole((prev) => ({
      ...prev,
      permissions: isAllSelected ? [] : allIds,
    }));
  };

  const submitNewRole = async () => {
    if (!newRole.name.trim()) {
      dispatch(
        showSnackBar({ text: t("roles.nameRequired"), severity: "error" })
      );
      return;
    }
    setSubmitting(true);
    const saved = await createNewRole(newRole);
    setSubmitting(false);
    if (saved.error) {
      dispatch(
        showSnackBar({
          text:
            saved?.error?.response?.data?.message ?? t("roles.createFailed"),
          severity: "error",
        })
      );
      return;
    }
    const created = {
      id: saved.data.result.id,
      name: saved.data.result.name,
    };
    setRoles((prev) => [...prev, created]);
    onChange(created);
    dispatch(
      showSnackBar({ text: t("roles.created"), severity: "success" })
    );
    closeModal();
  };

  const modalContent = (
    <>
      <div
        className="fixed inset-0 z-[500000000000000] bg-[gray] bg-opacity-30"
        onClick={closeModal}
      />
      <div className="fixed inset-0 z-[500000000000000] flex items-center justify-center">
        <div
          className="w-full max-w-2xl mx-4 bg-ui-surface rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-3 border-b border-ui-surface-sunken">
            <h3 className="text-lg font-semibold text-ui-text m-0">
              {t("roles.create")}
            </h3>
            <button
              onClick={closeModal}
              className="text-ui-text-subtle hover:text-ui-text-muted"
            >
              <IoCloseOutline size={22} />
            </button>
          </div>
          <div className="px-6 py-4 overflow-y-auto flex-1 flex flex-col gap-3">
            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">
                {t("roles.name")}
              </label>
              <input
                type="text"
                value={newRole.name}
                onChange={(e) =>
                  setNewRole((p) => ({ ...p, name: e.target.value }))
                }
                placeholder={t("roles.name")}
                className="w-full h-10 px-3 border border-ui-border rounded-lg text-sm text-ui-text outline-none focus:border-brand focus:ring-1 focus:ring-brand-light"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-ui-text-muted m-0">
                  {t("roles.permissions")}
                </p>
                {!loadingPermissions && permissions.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className="text-xs text-brand hover:text-brand-hover font-medium transition-colors"
                  >
                    {isAllSelected ? t("roles.deselectAll") : t("roles.selectAll")}
                  </button>
                )}
              </div>
              {loadingPermissions ? (
                <div className="grid grid-cols-2 gap-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="border border-ui-border rounded-lg p-3 animate-pulse"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-ui-border rounded" />
                        <div className="h-3.5 bg-ui-border rounded w-3/4" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {permissions.map((parent, i) => {
                    const moduleIds = [
                      parent.id,
                      ...(parent.children ?? []).map((c) => c.id),
                    ];
                    const checked = moduleIds.some((id) =>
                      newRole.permissions.includes(id)
                    );
                    return (
                      <div
                        key={i}
                        className="border border-ui-border rounded-lg p-3"
                      >
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="accent-brand"
                            checked={checked}
                            onChange={(e) => handleParentChange(e, moduleIds)}
                          />
                          <span className="text-sm font-medium text-ui-text">
                            {parent.description}
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 px-6 py-3 border-t border-ui-surface-sunken">
            <BoltButton
              label={t("common:actions.cancel")}
              variant="outline"
              size="md"
              onClick={closeModal}
            />
            <BoltButton
              label={t("roles.create")}
              variant="primary"
              size="md"
              loading={submitting}
              disabled={loadingPermissions}
              onClick={submitNewRole}
            />
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Autocomplete
        value={value || null}
        onChange={(event, newValue) => {
          if (typeof newValue === "string") {
            const matched = roles.find((r) => r.name === newValue);
            onChange(matched ?? null);
          } else if (newValue && newValue.inputValue) {
            setNewRole({ name: newValue.inputValue, permissions: [] });
            setModalOpen(true);
          } else {
            onChange(newValue);
          }
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const { inputValue } = params;
          const isExisting = options.some((o) => inputValue === o.name);
          if (inputValue !== "" && !isExisting) {
            filtered.push({
              inputValue,
              name: t("roles.addOption", { value: inputValue }),
            });
          }
          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={roles}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          if (option.inputValue) return option.inputValue;
          return option.name ?? "";
        }}
        renderOption={(props, option) => <li {...props}>{option.name}</li>}
        sx={autocompleteSx}
        freeSolo
        renderInput={(params) => (
          <TextField {...params} placeholder={t("roles.selectOrCreate")} />
        )}
      />
      {modalOpen && ReactDOM.createPortal(modalContent, document.body)}
    </>
  );
}
